// src/components/resources/tools/generators/RedditPost/RedditPostGenerator.tsx

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent, type FC } from 'react'
import { toast } from 'sonner'
import {
  ArrowBigDown,
  ArrowBigUp,
  Award,
  Download,
  MessageSquare,
  Palette,
  Share2,
  Upload,
} from 'lucide-react'
import { clsx } from 'clsx'
import Image from 'next/image'
import { sendResourceEvent, createEngagementTimers } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'
import { applyVote, formatCount, type VoteState } from './scoring'
import {
  normalizeHandle,
  normalizeSubreddit,
  REDDIT_COMMENT_CAPABILITIES,
  renameAuthorComments,
  toggleVoteById,
  type ThreadComment,
} from '../CommentThread/threadModel'
import { ThreadCommentEditor } from '../CommentThread/ThreadCommentEditor'
import { buildDefaultRedditComments, RedditCommentThread } from './RedditCommentThread'

export type { VoteState }

/**
 * Which mockup the tool renders: the single post card, or the comment-thread
 * view (nested comments with indent rails) — the follow-up this tool's FAQ
 * promised while it was post-only.
 */
export type RedditMockupMode = 'post' | 'comments'

/**
 * Configuration for the Reddit post mockup.
 */
export interface RedditConfig {
  /** Community handle without the leading `r/` (the layout prepends it). */
  subreddit: string
  /** Author handle without the leading `u/`. */
  username: string
  /** Optional post flair chip (e.g. "Discussion", "Guide"). */
  flair: string
  title: string
  body: string
  /** Base upvote score before any interactive vote is applied. */
  score: number
  comments: number
  /** Number of awards shown next to the award glyph (current gold-based system). */
  awards: number
  timestamp: string
  theme: 'light' | 'dark'
  avatarUrl?: string
  mediaUrl?: string
}

export const DEFAULT_CONFIG: RedditConfig = {
  subreddit: 'marketing',
  username: 'adsights',
  flair: 'Discussion',
  title: 'We replaced three mockup tools with one free generator — here’s the workflow',
  body: 'Spun up a full set of client-ready social proof in an afternoon: feed posts, chat threads, and review cards, all exported as clean PNGs straight into the deck. Curious what the rest of you use for campaign concept previews?',
  score: 1280,
  comments: 184,
  awards: 2,
  timestamp: '5h',
  theme: 'light',
}

interface PreviewProps {
  config: RedditConfig
  vote: VoteState
  onVote: (next: VoteState) => void
}

/**
 * A single Reddit post card, styled to the current ("new Reddit") layout:
 * a community byline, the title (+ optional flair), body/media, and a bottom
 * action bar with a horizontal vote pill, comment count, the current
 * gold-based Award control, and Share. Light and dark themes both ship.
 */
const RedditPostCard: FC<PreviewProps> = ({ config, vote, onVote }): JSX.Element => {
  // normalizeSubreddit strips a pasted r/ prefix, so the byline renders
  // "r/marketing" (never "r/r/marketing") and the fallback avatar initial
  // derives from the community name, not the prefix — same shared helper as
  // the comment-thread card.
  const subreddit = normalizeSubreddit(config.subreddit)
  const isDark = config.theme === 'dark'
  const displayScore = applyVote(config.score, vote)

  // Surfaces / text tokens per theme (Reddit dark uses ~#1a1a1b cards).
  const cardBg = isDark ? 'bg-[#1a1a1b] border-[#343536]' : 'bg-white border-gray-200'
  const titleText = isDark ? 'text-[#d7dadc]' : 'text-gray-900'
  const bodyText = isDark ? 'text-[#d7dadc]' : 'text-gray-800'
  const mutedText = isDark ? 'text-[#818384]' : 'text-gray-500'
  const pillBg = isDark ? 'bg-[#272729]' : 'bg-gray-100'
  const actionHover = isDark ? 'hover:bg-[#272729]' : 'hover:bg-gray-100'

  const upActive = vote === 'up'
  const downActive = vote === 'down'
  const scoreColor = upActive ? 'text-[#ff4500]' : downActive ? 'text-[#7193ff]' : isDark ? 'text-[#d7dadc]' : 'text-gray-800'

  return (
    <article className={clsx('mx-auto w-full max-w-2xl overflow-hidden rounded-md border shadow-xl', cardBg)}>
      <div className="p-3 sm:p-4">
        {/* Byline */}
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-orange-500 to-rose-500 text-[11px] font-bold text-white">
            {config.avatarUrl ? (
              <Image src={config.avatarUrl} alt="" width={24} height={24} className="h-full w-full object-cover" unoptimized />
            ) : (
              (subreddit[0] ?? 'r').toUpperCase()
            )}
          </div>
          <span className={clsx('text-[13px] font-bold', titleText)}>r/{subreddit}</span>
          <span className={clsx('text-[13px]', mutedText)}>·</span>
          <span className={clsx('truncate text-[12px]', mutedText)}>
            Posted by u/{normalizeHandle(config.username)}
            {' · '}{config.timestamp}
          </span>
          <button
            type="button"
            className="ml-auto flex-shrink-0 rounded-full bg-[#0079d3] px-3 py-1 text-[12px] font-bold text-white"
          >
            Join
          </button>
        </div>

        {/* Title + flair */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          {config.flair.trim().length > 0 && (
            <span className={clsx('rounded-full px-2 py-0.5 text-[11px] font-semibold', isDark ? 'bg-[#343536] text-[#d7dadc]' : 'bg-gray-200 text-gray-700')}>
              {config.flair}
            </span>
          )}
          <h2 className={clsx('text-[18px] font-medium leading-snug', titleText)}>{config.title}</h2>
        </div>

        {/* Body */}
        {config.body.trim().length > 0 && (
          <p className={clsx('mt-2 whitespace-pre-wrap text-[14px] leading-relaxed', bodyText)}>{config.body}</p>
        )}

        {/* Media */}
        {config.mediaUrl ? (
          <div className={clsx('mt-3 overflow-hidden rounded-md border', isDark ? 'border-[#343536]' : 'border-gray-200')}>
            <Image src={config.mediaUrl} alt="Post media" width={1000} height={600} className="w-full object-cover" style={{ maxHeight: '480px', height: 'auto' }} unoptimized />
          </div>
        ) : null}

        {/* Action bar — wraps on narrow widths so the pills aren't clipped by
            the card's `overflow-hidden` in the preview or the exported PNG. */}
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] font-bold">
          {/* Vote pill */}
          <div className={clsx('flex items-center gap-1 rounded-full px-1 py-1', pillBg)}>
            <button
              type="button"
              onClick={() => onVote(upActive ? null : 'up')}
              className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              aria-label={upActive ? 'Remove upvote' : 'Upvote'}
              aria-pressed={upActive}
            >
              <ArrowBigUp className={clsx('h-5 w-5', upActive ? 'fill-[#ff4500] text-[#ff4500]' : mutedText)} />
            </button>
            <span className={clsx('min-w-[2ch] text-center tabular-nums', scoreColor)}>{formatCount(displayScore)}</span>
            <button
              type="button"
              onClick={() => onVote(downActive ? null : 'down')}
              className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
              aria-label={downActive ? 'Remove downvote' : 'Downvote'}
              aria-pressed={downActive}
            >
              <ArrowBigDown className={clsx('h-5 w-5', downActive ? 'fill-[#7193ff] text-[#7193ff]' : mutedText)} />
            </button>
          </div>

          {/* Comments */}
          <div className={clsx('flex items-center gap-1.5 rounded-full px-3 py-2', pillBg, mutedText)}>
            <MessageSquare className="h-4 w-4" />
            <span>{formatCount(config.comments)} Comments</span>
          </div>

          {/* Award (current gold-based award control) */}
          <div className={clsx('flex items-center gap-1.5 rounded-full px-3 py-2 transition-colors', actionHover, mutedText)}>
            <Award className="h-4 w-4 text-[#ffb000]" />
            <span>{config.awards > 0 ? formatCount(config.awards) : 'Award'}</span>
          </div>

          {/* Share — always rendered so it's present in the exported PNG
              (html2canvas captures only what's on screen). */}
          <div className={clsx('flex items-center gap-1.5 rounded-full px-3 py-2 transition-colors', actionHover, mutedText)}>
            <Share2 className="h-4 w-4" />
            <span>Share</span>
          </div>
        </div>
      </div>
    </article>
  )
}

/**
 * Props for the Reddit Post Mockup Generator.
 *
 * `trackingSlug` lets future landing-page variants (e.g. a classic/new/mobile
 * layout split) attribute their own export/interaction analytics; the umbrella
 * route omits it and uses the umbrella slug.
 */
export interface RedditPostGeneratorProps {
  trackingSlug?: string
}

const TEXT_INPUT_CLASS =
  'rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white'

/**
 * Main component for the Reddit Post Mockup Generator. Renders a current-style
 * Reddit post card with editable community, author, title/body, flair, vote
 * score, comment + award counts, and theme, and exports a retina PNG.
 */
export function RedditPostGenerator({ trackingSlug = 'reddit-post-mockup-generator' }: RedditPostGeneratorProps = {}): JSX.Element {
  const [config, setConfig] = useState<RedditConfig>(DEFAULT_CONFIG)
  const [mode, setMode] = useState<RedditMockupMode>('post')
  const [commentThread, setCommentThread] = useState<ThreadComment[]>(buildDefaultRedditComments)
  // Lower-priority snapshot of everything that drives the heavy preview card
  // (config + mode + comment tree). Controls (the aside) keep reading live
  // state, so subreddit/title/body/comment keystrokes commit synchronously
  // while the card re-renders at a lower priority — shortening the long
  // interaction task behind mobile INP. `vote` is NOT deferred: it's toggled
  // by clicking the upvote/downvote arrows INSIDE the preview (a direct
  // preview interaction, not a controls keystroke), so it stays live for
  // instant feedback. html2canvas export waits for the deferred snapshot to
  // converge before reading the DOM (see handleExport), so resting output and
  // export match the live state.
  const view = useMemo(() => ({ config, mode, commentThread }), [config, mode, commentThread])
  const { deferred: deferredView, isStale } = usePreviewDeferral(view)
  const deferredConfig = deferredView.config
  const previewStaleRef = useStaleRef(isStale)
  // Start neutral so the displayed/exported score equals the configured
  // Upvotes value — initialising to 'up' silently exported score + 1.
  const [vote, setVote] = useState<VoteState>(null)
  // Shared export lifecycle: warm html2canvas on mount, re-entrancy guard,
  // and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()
  const mediaInputRef = useRef<HTMLInputElement>(null)
  const previewRef = useRef<HTMLDivElement>(null)

  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  const handleUpload = useCallback(
    (key: 'avatarUrl' | 'mediaUrl', interaction: string) =>
      (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0]
        if (!file) return
        // Guard against oversized uploads — a large file becomes an unbounded
        // data URL in React state (tab hang / OOM). 10 MB ceiling.
        const MAX_BYTES = 10 * 1024 * 1024
        if (file.size > MAX_BYTES) {
          toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
          event.target.value = ''
          return
        }
        const reader = new FileReader()
        reader.onerror = () => toast.error('Could not read that image. Please try another file.')
        reader.onload = e => {
          setConfig(prev => ({ ...prev, [key]: e.target?.result as string }))
          sendResourceEvent('interact', {
            resourceType: 'tool',
            resourceSlug: trackingSlug,
            engagementMs: getDeltaEngagementMs(),
            params: { tool_type: 'generator', interaction_type: interaction, file_type: file.type, file_size: file.size },
          })
        }
        reader.readAsDataURL(file)
      },
    [trackingSlug, getDeltaEngagementMs],
  )

  const handleExport = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) {
      toast.error('Preview not found. Please ensure the mockup is visible.')
      return
    }
    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge so the PNG can't ship a previous frame. Fail loudly if it can't
    // settle within the ceiling rather than export a stale frame. Flip the
    // loading state only AFTER the gate so it tracks the capture, not the wait.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `reddit-${mode === 'comments' ? 'comment-thread' : 'post'}-mockup-${Date.now()}`,
      trackingSlug,
      engagementMs: getTotalEngagementMs(),
      onSuccess: () => toast.success('Mockup exported successfully!'),
      onError: () => toast.error('Failed to export mockup. Please try again.'),
    })
  }), [runExport, trackingSlug, mode, getTotalEngagementMs, previewStaleRef])

  // Per-comment vote arrows in the comment-thread preview — the same
  // direct-preview interaction as the post card's vote pill. The arrows
  // report only the clicked DIRECTION; the tri-state toggle resolves against
  // the LIVE tree inside the functional updater. The card renders from the
  // deferred snapshot, so deriving "next" from the rendered `comment.vote`
  // let a rapid second click read the stale pre-commit state and send 'up'
  // twice instead of clearing the vote (`r3564364629`).
  const handleCommentVote = useCallback((commentId: string, direction: 'up' | 'down'): void => {
    setCommentThread(prev => toggleVoteById(prev, commentId, direction))
  }, [])

  // Changing the post author renames the OP-badged comments too: `byAuthor`
  // (the OP badge) means "written by the post author", so the handle follows —
  // otherwise an export could pair one author's byline with another name on
  // the OP-badged comments.
  const handleUsernameChange = useCallback((value: string): void => {
    setConfig(prev => ({ ...prev, username: value }))
    setCommentThread(prev => renameAuthorComments(prev, value))
  }, [])

  const switchMode = useCallback(
    (next: RedditMockupMode): void => {
      setMode(next)
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: 'mode_switch', mode: next },
      })
    },
    [trackingSlug, getDeltaEngagementMs],
  )

  const themeLabel = useMemo(() => (config.theme === 'dark' ? 'Dark' : 'Light'), [config.theme])

  const numberFields: { key: keyof Pick<RedditConfig, 'score' | 'comments' | 'awards'>; label: string }[] = [
    { key: 'score', label: 'Upvotes' },
    { key: 'comments', label: 'Comments' },
    { key: 'awards', label: 'Awards' },
  ]

  // Memoize the preview ELEMENT so a per-keystroke urgent re-render (deferredConfig
  // referentially unchanged, vote unchanged) reuses it and React bails out of
  // re-rendering the heavy card — it renders only on the low-priority deferred pass.
  // `vote` is a dep so an in-preview vote click still updates immediately. Without
  // this bail-out, useDeferredValue double-renders the card and *raises* INP.
  const redditCard = useMemo(
    () => <RedditPostCard config={deferredConfig} vote={vote} onVote={setVote} />,
    [deferredConfig, vote],
  )

  // Comment-thread card renders from the same deferred snapshot; it has no
  // live in-preview interaction (unlike the post card's vote arrows).
  const commentCard = useMemo(
    () => (
      <RedditCommentThread
        config={deferredView.config}
        comments={deferredView.commentThread}
        onVote={handleCommentVote}
      />
    ),
    [deferredView, handleCommentVote],
  )

  const isCommentsMode = mode === 'comments'

  return (
    <div className="flex w-full flex-col gap-6 lg:flex-row">
      <aside className="w-full space-y-6 rounded-3xl border border-slate-200 bg-white/80 p-4 shadow-lg backdrop-blur lg:w-[340px] dark:border-slate-800 dark:bg-slate-900/60">
        {/* Mockup mode */}
        <section aria-labelledby="reddit-mode" className="space-y-2">
          <h3 id="reddit-mode" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Mockup Mode
          </h3>
          <div
            role="group"
            aria-labelledby="reddit-mode"
            className="grid grid-cols-2 gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-900"
          >
            {(
              [
                { id: 'post', label: 'Post card' },
                { id: 'comments', label: 'Comment thread' },
              ] as const
            ).map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => switchMode(option.id)}
                aria-pressed={mode === option.id}
                className={clsx(
                  'rounded-md px-2 py-1.5 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900',
                  mode === option.id
                    ? 'bg-slate-900 text-white shadow-sm dark:bg-sky-500'
                    : 'bg-transparent text-slate-600 hover:bg-slate-200 dark:text-slate-300 dark:hover:bg-slate-800',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>
        </section>

        {/* Community & author */}
        <section aria-labelledby="reddit-community" className="space-y-2">
          <h3 id="reddit-community" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Community &amp; Author
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="flex flex-col gap-0.5">
              <span className="font-medium text-slate-700 dark:text-slate-200">Subreddit (r/)</span>
              <input type="text" value={config.subreddit} onChange={e => setConfig(p => ({ ...p, subreddit: e.target.value }))} maxLength={50} className={TEXT_INPUT_CLASS} />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="font-medium text-slate-700 dark:text-slate-200">Username (u/)</span>
              <input type="text" value={config.username} onChange={e => handleUsernameChange(e.target.value)} maxLength={50} className={TEXT_INPUT_CLASS} />
            </label>
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Timestamp</span>
              <input type="text" value={config.timestamp} onChange={e => setConfig(p => ({ ...p, timestamp: e.target.value }))} className={TEXT_INPUT_CLASS} placeholder="5h" maxLength={20} />
            </label>
          </div>
        </section>

        {/* Content */}
        <section aria-labelledby="reddit-content" className="space-y-2">
          <h3 id="reddit-content" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Post Content
          </h3>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {!isCommentsMode && (
              <label className="flex flex-col gap-0.5 col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Flair</span>
                <input type="text" value={config.flair} onChange={e => setConfig(p => ({ ...p, flair: e.target.value }))} className={TEXT_INPUT_CLASS} placeholder="Discussion" maxLength={40} />
              </label>
            )}
            <label className="flex flex-col gap-0.5 col-span-2">
              <span className="font-medium text-slate-700 dark:text-slate-200">Title</span>
              <input type="text" value={config.title} onChange={e => setConfig(p => ({ ...p, title: e.target.value }))} maxLength={300} className={TEXT_INPUT_CLASS} />
            </label>
            {!isCommentsMode && (
              <label className="flex flex-col gap-0.5 col-span-2">
                <span className="font-medium text-slate-700 dark:text-slate-200">Body</span>
                <textarea value={config.body} onChange={e => setConfig(p => ({ ...p, body: e.target.value }))} maxLength={4000} className={clsx(TEXT_INPUT_CLASS, 'min-h-[80px] leading-relaxed')} />
              </label>
            )}
          </div>
          {!isCommentsMode && (
            <>
              <button
                type="button"
                onClick={() => mediaInputRef.current?.click()}
                className="flex w-full items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 bg-white py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <Upload className="h-3 w-3" />
                {config.mediaUrl ? 'Replace image' : 'Add post image'}
              </button>
              <input type="file" accept="image/*" ref={mediaInputRef} onChange={handleUpload('mediaUrl', 'media_upload')} className="hidden" />
              {config.mediaUrl && (
                <button type="button" onClick={() => setConfig(p => ({ ...p, mediaUrl: undefined }))} className="text-xs font-medium text-slate-500 underline hover:text-slate-700 dark:text-slate-400">
                  Remove image
                </button>
              )}
            </>
          )}
        </section>

        {/* Comment thread (comments mode only) — the fully editable nested
            tree: every comment and reply can be edited, removed, or replied
            to (up to the Reddit depth cap), with OP badges and negative
            scores allowed, matching real Reddit threads. */}
        {isCommentsMode && (
          <section aria-labelledby="reddit-comments-editor" className="space-y-2">
            <h3 id="reddit-comments-editor" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Comment Thread
            </h3>
            <ThreadCommentEditor
              comments={commentThread}
              onCommentsChange={setCommentThread}
              capabilities={REDDIT_COMMENT_CAPABILITIES}
              postAuthor={config.username}
            />
          </section>
        )}

        {/* Engagement */}
        <section aria-labelledby="reddit-engagement" className="space-y-2">
          <h3 id="reddit-engagement" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {isCommentsMode ? 'Style' : 'Engagement'}
          </h3>
          {!isCommentsMode && (
          <div className="grid grid-cols-3 gap-2 text-xs">
            {numberFields.map(({ key, label }) => (
              <label key={key} className="flex flex-col gap-0.5">
                <span className="font-medium text-slate-700 dark:text-slate-200">{label}</span>
                <input
                  type="number"
                  min={0}
                  value={config[key]}
                  // Clamp to >= 0: `min` alone doesn't stop a typed negative,
                  // which would render/export "-5 Comments" against the
                  // non-negative engagement model the controls advertise.
                  onChange={e => setConfig(p => ({ ...p, [key]: Math.max(0, Number.parseInt(e.target.value, 10) || 0) }))}
                  className={TEXT_INPUT_CLASS}
                />
              </label>
            ))}
          </div>
          )}
          {!isCommentsMode && (
          <label className="flex flex-col gap-0.5 text-xs">
            <span className="font-medium text-slate-700 dark:text-slate-200">Community avatar</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleUpload('avatarUrl', 'avatar_upload')}
              className="rounded border border-dashed border-slate-300 bg-white px-3 py-2 text-xs text-slate-900 shadow-sm file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-2 file:text-xs file:font-medium file:text-white dark:border-slate-700 dark:bg-slate-900 dark:file:bg-sky-500"
            />
          </label>
          )}
          <div className="mt-1 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center gap-2">
              <Palette className="h-3 w-3 text-slate-500" />
              <span className="font-medium text-slate-700 dark:text-slate-200">Theme</span>
            </div>
            <button
              type="button"
              onClick={() => setConfig(p => ({ ...p, theme: p.theme === 'dark' ? 'light' : 'dark' }))}
              className="rounded bg-slate-900 px-2 py-1 text-xs font-medium text-white shadow-sm transition hover:bg-slate-700 dark:bg-sky-500 dark:hover:bg-sky-400"
            >
              {themeLabel}
            </button>
          </div>
        </section>

        {/* Export */}
        <section aria-labelledby="reddit-export" className="space-y-2">
          <h3 id="reddit-export" className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Export
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-300">
            Download the mockup as a high-resolution PNG (captured at 2&times; retina density) — deck-ready, with no watermark.
          </p>
          <ExportButton
            isExporting={isExporting}
            onExport={() => void handleExport()}
            icon={<Download className="h-3 w-3" />}
            className="w-full gap-1 rounded-lg bg-slate-900 px-2 py-2 text-xs font-semibold text-white shadow-lg transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-sky-500 dark:hover:bg-sky-400"
          />
        </section>
      </aside>

      <div
        className={clsx(
          'relative flex flex-1 justify-center rounded-3xl border border-white/10 p-6 shadow-2xl dark:border-slate-800',
          // Comments mode: the thread editor makes the controls column tall, so
          // vertical centering would push the card halfway down an empty panel —
          // pin it to the top instead. Post mode keeps the centered layout.
          deferredView.mode === 'comments' ? 'items-start' : 'items-center',
        )}
        style={{ background: deferredConfig.theme === 'dark' ? '#030303' : 'linear-gradient(135deg,#dae0e6,#f6f7f8)' }}
      >
        {/* Capture region matched to the card width so the PNG crops tight. The
            card renders from the deferred snapshot; `vote` stays live so
            clicking the in-preview arrows feels instant. The rendered card
            follows the DEFERRED mode so a mode switch and its card swap in the
            same low-priority commit (export gates on the same snapshot). */}
        <div className="relative z-10 mx-auto w-full max-w-2xl" ref={previewRef} data-testid="reddit-preview">
          {deferredView.mode === 'comments' ? commentCard : redditCard}
        </div>
      </div>
    </div>
  )
}